import { useEffect, useState } from 'react';
import { Building2, Loader2 } from 'lucide-react';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import SelectField from '../../../components/Forms/Input/SelectField';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import BranchService from '../../../services/branch/BranchService';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';
import { IBranchDropdown } from '../../../types/branches/branchInterfaces';
import RestaurantAppearanceSettingsForm from './RestaurantAppearanceSettingsForm';
import RestaurantGeneralSettingsForm from './RestaurantGeneralSettingsForm';
import RestaurantInventorySettingsForm from './RestaurantInventorySettingsForm';
import RestaurantKitchenSettingsForm from './RestaurantKitchenSettingsForm';
import RestaurantPosSettingsForm from './RestaurantPosSettingsForm';
import RestaurantPrintingSettingsForm from './RestaurantPrintingSettingsForm';
import RestaurantReservationSettingsForm from './RestaurantReservationSettingsForm';
import RestaurantSettingsSidebar from './RestaurantSettingsSidebar';
import { RestaurantSettingsSectionKey } from './RestaurantSettingsTypes';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';

const RestaurantSettingsPage = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [activeSection, setActiveSection] =
    useState<RestaurantSettingsSectionKey>('general');
  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [branchError, setBranchError] = useState('');

  useEffect(() => {
    const loadBranches = async () => {
      setLoadingBranches(true);
      setBranchError('');
      try {
        const data = canPickBranch
          ? await BranchService.fetchDropdown()
          : [await BranchService.fetchMyBranch()];
        setBranches(data);
        if (data.length > 0) setBranchId(data[0].id);
      } catch (error: any) {
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        );
      } finally {
        setLoadingBranches(false);
      }
    };

    loadBranches();
  }, [canPickBranch]);

  const renderSection = () => {
    if (!branchId) return null;

    switch (activeSection) {
      case 'general':
        return <RestaurantGeneralSettingsForm branchId={branchId} />;
      case 'pos':
        return <RestaurantPosSettingsForm branchId={branchId} />;
      case 'kitchen':
        return <RestaurantKitchenSettingsForm branchId={branchId} />;
      case 'inventory':
        return <RestaurantInventorySettingsForm branchId={branchId} />;
      case 'reservations':
        return <RestaurantReservationSettingsForm branchId={branchId} />;
      case 'printing':
        return <RestaurantPrintingSettingsForm branchId={branchId} />;
      case 'appearance':
        return <RestaurantAppearanceSettingsForm branchId={branchId} />;
    }
  };

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <div className="border-b border-stroke bg-white px-5 py-5 dark:border-strokedark dark:bg-boxdark sm:px-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            {/* Encabezado */}
            <div className="flex items-center gap-3">
              <span className="rounded-xl border border-primary/15 bg-primary/10 p-2 text-primary">
                <Building2 className="h-5 w-5" />
              </span>

              <div>
                <h1 className="text-xl font-semibold text-black dark:text-white">
                  Configuración del restaurante
                </h1>

                <p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">
                  Administre las reglas operativas de cada sucursal por sección.
                </p>
              </div>
            </div>

            {/* Selector de sucursal (solo ADMIN/SUPPORT/ACCOUNTANT) */}
            {canPickBranch && (
              <div className="w-full lg:w-80">
                {loadingBranches ? (
                  <div className="flex h-[74px] items-center justify-center gap-2 text-sm text-bodydark2 dark:text-bodydark">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    Cargando sucursales...
                  </div>
                ) : (
                  <SelectField
                    label="Sucursal"
                    iconKey="list"
                    name="restaurantSettingsBranchId"
                    value={branchId}
                    options={branches.map((branch) => ({
                      value: branch.id,
                      label: branch.name,
                    }))}
                    placeholder="Seleccione una sucursal"
                    onChange={(event) => setBranchId(Number(event.target.value))}
                  />
                )}
              </div>
            )}

            {branchError && (
              <p className="mt-2 text-sm font-medium text-danger">
                {branchError}
              </p>
            )}
          </div>
        </div>

        {!loadingBranches && !branchError && branches.length === 0 ? (
          <div className="px-6 py-12 text-center text-bodydark2 dark:text-bodydark">
            No hay sucursales disponibles para configurar.
          </div>
        ) : (
          branchId && (
            <div className="flex min-h-[520px] flex-col md:flex-row">
              <RestaurantSettingsSidebar
                activeSection={activeSection}
                onChange={setActiveSection}
              />

              <main
                className="min-w-0 flex-1 bg-white dark:bg-boxdark"
                key={`${branchId}-${activeSection}`}
              >
                {renderSection()}
              </main>
            </div>
          )
        )}
      </SectionWrapper>
    </WorkspaceLayout>
  );
};

export default RestaurantSettingsPage;
